import {
  applyActionCode,
  createUserWithEmailAndPassword,
  getRedirectResult,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithRedirect,
  signInWithPopup,
  signOut,
  type UserCredential,
  updateProfile
} from "firebase/auth";
import { FirebaseError } from "firebase/app";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "@/config/firebase";
import {
  clearAccountActiveInSession,
  markAccountActiveInSession
} from "@/features/auth/authSession";
import type { GoogleSignUpFormValues, SignUpFormValues, UserProfile } from "@/features/auth/types";

const GOOGLE_SIGN_UP_DRAFT_STORAGE_KEY = "geoanalytics.googleSignUpDraft";

export type EmailDeliveryState = "queued" | "pending_transport";

export type GoogleProfileCompletionResult = {
  created: boolean;
  verificationCodeRequested: boolean;
  emailDelivery: EmailDeliveryState;
  status: UserProfile["status"];
  email: string;
};

export type GoogleAuthCompletionResult = {
  user: UserCredential["user"];
  profileResult: GoogleProfileCompletionResult;
};

type StoredGoogleSignUpDraft = {
  termsAcceptedAt: string;
  privacyAcceptedAt: string;
};

type CompleteGoogleSignUpResponse = {
  ok: boolean;
  profileCreated: boolean;
  status: UserProfile["status"];
  verificationCodeCreated: boolean;
  emailDelivery: EmailDeliveryState;
  email: string;
};

type CompleteEmailSignUpInput = {
  firstName: string;
  lastName: string;
  birthDate: string;
  cpf: string;
  secretQuestionId: string;
  secretAnswer: string;
  termsAcceptedAt: string;
  privacyAcceptedAt: string;
};

type CompleteEmailSignUpResponse = {
  ok: boolean;
  profileCreated: boolean;
  status: UserProfile["status"];
  verificationCodeCreated: boolean;
  emailDelivery: EmailDeliveryState;
  email: string;
  expiresAt: string;
};

type RequestAccountVerificationCodeResponse = {
  ok: boolean;
  emailDelivery: EmailDeliveryState;
  email: string;
  expiresAt: string;
};

type VerifyAccountCodeResponse = {
  ok: boolean;
  status: UserProfile["status"];
};

type RequestPasswordRecoveryCodeResponse = {
  ok: boolean;
  codeSent: boolean;
  emailDelivery?: EmailDeliveryState;
  expiresAt?: string;
};

type VerifyPasswordRecoveryCodeResponse = {
  ok: boolean;
  recoveryToken: string;
  secretQuestionId: string;
  secretQuestionPrompt: string;
};

type VerifyPasswordRecoverySecretResponse = {
  ok: boolean;
  resetToken: string;
};

export type SyncCurrentUserActivationResponse = {
  ok: true;
  profileFound: boolean;
  profileCreated: boolean;
  activated: boolean;
  status: UserProfile["status"] | null;
};

function createGoogleProvider() {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({
    prompt: "select_account"
  });
  return provider;
}

function saveGoogleSignUpDraft(values: GoogleSignUpFormValues) {
  const now = new Date().toISOString();
  const draft: StoredGoogleSignUpDraft = {
    termsAcceptedAt: values.acceptedTerms ? now : "",
    privacyAcceptedAt: values.acceptedPrivacy ? now : ""
  };

  sessionStorage.setItem(GOOGLE_SIGN_UP_DRAFT_STORAGE_KEY, JSON.stringify(draft));
}

function getGoogleSignUpDraft() {
  const rawDraft = sessionStorage.getItem(GOOGLE_SIGN_UP_DRAFT_STORAGE_KEY);

  if (!rawDraft) {
    return null;
  }

  try {
    return JSON.parse(rawDraft) as StoredGoogleSignUpDraft;
  } catch {
    sessionStorage.removeItem(GOOGLE_SIGN_UP_DRAFT_STORAGE_KEY);
    return null;
  }
}

function clearGoogleSignUpDraft() {
  sessionStorage.removeItem(GOOGLE_SIGN_UP_DRAFT_STORAGE_KEY);
}

function shouldFallbackToRedirect(error: unknown) {
  return (
    error instanceof FirebaseError &&
    ["auth/popup-blocked", "auth/operation-not-supported-in-this-environment"].includes(error.code)
  );
}

function createGoogleCompletionResult(
  credential: UserCredential,
  profileResult: GoogleProfileCompletionResult
): GoogleAuthCompletionResult {
  return {
    user: credential.user,
    profileResult
  };
}

async function refreshCurrentAuthUser() {
  if (!auth.currentUser) {
    return;
  }

  await auth.currentUser.reload();
  await auth.currentUser.getIdToken(true);
}

async function completeGoogleSignUpForCredential(credential: UserCredential) {
  const googleSignUpDraft = getGoogleSignUpDraft();

  if (!googleSignUpDraft) {
    throw new Error("Dados complementares do cadastro Google não foram encontrados.");
  }

  const completeSignUp = httpsCallable<StoredGoogleSignUpDraft, CompleteGoogleSignUpResponse>(
    functions,
    "geoApp-completeGoogleSignUp"
  );
  const result = await completeSignUp(googleSignUpDraft);

  clearGoogleSignUpDraft();

  return createGoogleCompletionResult(credential, {
    created: result.data.profileCreated,
    verificationCodeRequested: result.data.verificationCodeCreated,
    emailDelivery: result.data.emailDelivery,
    status: result.data.status,
    email: result.data.email
  });
}

async function ensureExistingGoogleProfile(credential: UserCredential) {
  const activationSync = await syncCurrentUserActivation();

  if (!activationSync.profileFound || !activationSync.status) {
    await signOut(auth);
    throw new Error("Complete o cadastro antes de entrar com Google.");
  }

  return createGoogleCompletionResult(credential, {
    created: false,
    verificationCodeRequested: false,
    emailDelivery: "queued",
    status: activationSync.status,
    email: credential.user.email ?? ""
  });
}

export async function signUpWithEmail(values: SignUpFormValues) {
  const credential = await createUserWithEmailAndPassword(auth, values.email, values.password);
  const displayName = `${values.firstName.trim()} ${values.lastName.trim()}`.trim();

  await updateProfile(credential.user, { displayName });

  const completeSignUp = httpsCallable<CompleteEmailSignUpInput, CompleteEmailSignUpResponse>(
    functions,
    "geoApp-completeEmailSignUp"
  );
  const result = await completeSignUp({
    firstName: values.firstName,
    lastName: values.lastName,
    birthDate: values.birthDate,
    cpf: values.cpf,
    secretQuestionId: values.secretQuestionId,
    secretAnswer: values.secretAnswer,
    termsAcceptedAt: new Date().toISOString(),
    privacyAcceptedAt: new Date().toISOString()
  });

  return {
    user: credential.user,
    emailDelivery: result.data.emailDelivery,
    email: result.data.email,
    expiresAt: result.data.expiresAt
  };
}

export async function signInWithEmail(email: string, password: string) {
  return signInWithEmailAndPassword(auth, email, password);
}

export async function signInWithGoogle() {
  try {
    const credential = await signInWithPopup(auth, createGoogleProvider());
    return ensureExistingGoogleProfile(credential);
  } catch (error) {
    if (shouldFallbackToRedirect(error)) {
      await signInWithRedirect(auth, createGoogleProvider());
      return null;
    }

    throw error;
  }
}

export async function signUpWithGoogle(values: GoogleSignUpFormValues) {
  saveGoogleSignUpDraft(values);

  try {
    const credential = await signInWithPopup(auth, createGoogleProvider());
    return await completeGoogleSignUpForCredential(credential);
  } catch (error) {
    if (shouldFallbackToRedirect(error)) {
      await signInWithRedirect(auth, createGoogleProvider());
      return null;
    }

    throw error;
  }
}

export async function completeGoogleRedirectSignIn() {
  const credential = await getRedirectResult(auth);

  if (!credential) {
    return null;
  }

  const profileResult = getGoogleSignUpDraft()
    ? await completeGoogleSignUpForCredential(credential)
    : await ensureExistingGoogleProfile(credential);

  return profileResult;
}

export async function activateEmailAccount(oobCode: string) {
  await applyActionCode(auth, oobCode);

  if (auth.currentUser) {
    await refreshCurrentAuthUser();
    await syncCurrentUserActivation();
  }
}

export async function activateGoogleAccount(token: string) {
  const activate = httpsCallable(functions, "geoApp-activateGoogleAccount");
  await activate({ token });
  await refreshCurrentAuthUser().catch(() => undefined);
}

export async function requestAccountVerificationCode() {
  const requestCode = httpsCallable<undefined, RequestAccountVerificationCodeResponse>(
    functions,
    "geoApp-requestAccountVerificationCode"
  );
  const result = await requestCode();

  return result.data;
}

export async function verifyAccountCode(code: string) {
  const verifyCode = httpsCallable<{ code: string }, VerifyAccountCodeResponse>(
    functions,
    "geoApp-verifyAccountCode"
  );
  const result = await verifyCode({ code });

  if (result.data.status === "active" && auth.currentUser) {
    markAccountActiveInSession(auth.currentUser.uid);
    await refreshCurrentAuthUser().catch(() => undefined);
  }

  return result.data;
}

export async function syncCurrentUserActivation() {
  await refreshCurrentAuthUser();

  const syncActivation = httpsCallable<undefined, SyncCurrentUserActivationResponse>(
    functions,
    "geoApp-syncCurrentUserActivation"
  );
  const result = await syncActivation();

  if (result.data.status === "active" && auth.currentUser) {
    markAccountActiveInSession(auth.currentUser.uid);
  }

  return result.data;
}

export async function requestActivationResend(email: string) {
  void email;
  const result = await requestAccountVerificationCode();
  return { emailDelivery: result.emailDelivery };
}

export async function requestPasswordRecoveryCode(email: string) {
  const requestCode = httpsCallable<{ email: string }, RequestPasswordRecoveryCodeResponse>(
    functions,
    "geoApp-requestPasswordRecoveryCode"
  );
  const result = await requestCode({ email });

  return result.data;
}

export async function verifyPasswordRecoveryCode(email: string, code: string) {
  const verifyCode = httpsCallable<
    { email: string; code: string },
    VerifyPasswordRecoveryCodeResponse
  >(functions, "geoApp-verifyPasswordRecoveryCode");
  const result = await verifyCode({ email, code });

  return result.data;
}

export async function verifyPasswordRecoverySecret(
  email: string,
  recoveryToken: string,
  secretAnswer: string
) {
  const verifySecret = httpsCallable<
    { email: string; recoveryToken: string; secretAnswer: string },
    VerifyPasswordRecoverySecretResponse
  >(functions, "geoApp-verifyPasswordRecoverySecret");
  const result = await verifySecret({ email, recoveryToken, secretAnswer });

  return result.data;
}

export async function resetPasswordWithRecoveryCode(
  email: string,
  resetToken: string,
  newPassword: string
) {
  const resetPassword = httpsCallable<
    { email: string; resetToken: string; newPassword: string },
    { ok: boolean }
  >(functions, "geoApp-resetPasswordWithRecoveryCode");
  await resetPassword({ email, resetToken, newPassword });
}

export async function signOutOfGeoAnalytics() {
  if (auth.currentUser) {
    clearAccountActiveInSession(auth.currentUser.uid);
  }

  await signOut(auth);
}

export function getActivationEmailErrorMessage(error: unknown) {
  if (!(error instanceof FirebaseError)) {
    return null;
  }

  if (error.code === "functions/resource-exhausted") {
    return "Aguarde alguns minutos antes de solicitar um novo código.";
  }

  if (error.code === "functions/unauthenticated") {
    return "Entre novamente no GeoAnalytics antes de solicitar um novo código.";
  }

  if (error.code === "functions/deadline-exceeded") {
    return "O código expirou. Solicite um novo código e tente novamente.";
  }

  if (error.code === "functions/invalid-argument") {
    return "Revise os dados informados e tente novamente.";
  }

  if (error.code === "functions/already-exists") {
    return "Já existe uma conta vinculada a estes dados. Revise as informações ou use o login.";
  }

  if (error.code === "functions/failed-precondition") {
    return "A conta não possui todos os requisitos de segurança configurados para esta ação.";
  }

  if (error.code === "functions/permission-denied") {
    return "Os dados de segurança informados não conferem.";
  }

  return null;
}

export function getGoogleAuthErrorMessage(error: unknown) {
  if (error instanceof FirebaseError) {
    if (error.code === "auth/popup-closed-by-user") {
      return "A janela do Google foi fechada antes da conclusão do login.";
    }

    if (error.code === "auth/cancelled-popup-request") {
      return "Uma tentativa anterior de login com Google ainda estava em andamento.";
    }

    if (error.code === "auth/popup-blocked") {
      return "O navegador bloqueou a janela do Google. Libere pop-ups para o GeoAnalytics e tente novamente.";
    }

    if (error.code === "auth/unauthorized-domain") {
      return "Este domínio ainda não está autorizado no Firebase Authentication para login com Google.";
    }

    if (error.code === "auth/account-exists-with-different-credential") {
      return "Este e-mail já existe com outro método de acesso. Entre com e-mail e senha ou vincule o Google no perfil.";
    }

    if (error.code === "auth/network-request-failed") {
      return "Falha de rede ao falar com o Google. Verifique a conexão e tente novamente.";
    }

    if (error.code.startsWith("functions/")) {
      return `O login com Google foi autenticado, mas a sincronização da conta falhou no backend (${error.code}).`;
    }

    return `Não foi possível concluir o login com Google (${error.code}).`;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível iniciar o acesso com Google agora.";
}
